import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, Cpu, Rocket, ChevronRight, Sparkles } from "lucide-react";
import { INDUSTRIES } from "@/lib/data";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { SiSap, SiSnowflake, SiDatabricks } from "react-icons/si";
import { Cloud, Server } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLines, Reveal, CountUp, ChapterTag, useSpotlight, EASE } from "@/components/motion";

const HERO_IMG = "https://images.unsplash.com/photo-1621264448270-9ef00e88a935?q=80&w=1800&auto=format&fit=crop";

const PILLARS = [
  {
    slug: "endur-consulting",
    icon: Cpu,
    title: "ETRM & Openlink Endur Consulting",
    items: ["Endur strategy & roadmap", "Functional design & business analysis", "Implementation & upgrade", "Testing, cutover & hyper-care"],
    cta: "Explore the practice",
    href: "/services/endur-consulting",
    highlight: true,
  },
  {
    slug: "digital-transformation",
    icon: Rocket,
    title: "Trading Technology & Transformation",
    items: ["Trading platform strategy", "System integration & data", "Cloud modernisation & analytics", "AI-enabled trading capability"],
    cta: "Explore the practice",
    href: "/services/digital-transformation",
  },
];

const INDUSTRIES_STRIP = INDUSTRIES.slice(0, 7).map((i) => ({ name: i.name, icon: i.icon }));

const TECH_STRIP = [
  { Icon: Cpu, label: "Openlink Endur" },
  { Icon: SiSap, label: "SAP" },
  { Icon: Cloud, label: "Microsoft Azure" },
  { Icon: SiSnowflake, label: "Snowflake" },
  { Icon: Server, label: "AWS" },
  { Icon: SiDatabricks, label: "Databricks" },
];

const MARQUEE_ITEMS = [
  "ETRM",
  "CTRM",
  "Openlink Endur",
  "Trading Technology",
  "Cloud & Data",
  "AI-Enabled Trading",
  "Energy",
  "Precision",
  "Excellence",
];

function PillarCard({ p, i }) {
  const { ref, pos, onMouseMove } = useSpotlight();
  const Icon = p.icon;
  return (
    <Reveal delay={i * 0.1} className="h-full">
      <div
        ref={ref}
        onMouseMove={onMouseMove}
        data-testid={`home-pillar-${p.slug}`}
        className={`clip-corner relative h-full overflow-hidden border p-8 md:p-10 transition-colors duration-500 group ${
          p.highlight
            ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]"
            : "border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40"
        }`}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(520px circle at ${pos.x}px ${pos.y}px, rgba(212,175,55,0.10), transparent 70%)` }}
        />
        <div className="relative">
          <div className="flex items-center justify-between mb-8">
            <Icon className="text-[#D4AF37]" size={26} strokeWidth={1.3} />
            <span className="font-mono text-xs text-slate-500 tracking-[0.3em]">P.0{i + 1}</span>
          </div>
          <h3 className="font-display text-xl md:text-2xl text-white font-medium tracking-tight">{p.title}</h3>
          <ul className="mt-6 space-y-2.5">
            {p.items.map((it) => (
              <li key={it} className="text-sm text-slate-400">— {it}</li>
            ))}
          </ul>
          <Link
            to={p.href}
            className="mt-8 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-mono text-slate-400 group-hover:text-[#D4AF37] transition-colors"
          >
            {p.cta} <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function InsightCard({ p, i }) {
  const { ref, pos, onMouseMove } = useSpotlight();
  return (
    <Reveal delay={i * 0.08} className="h-full">
      <Link
        to={`/insights/${p.slug}`}
        data-testid={`insight-card-${p.slug}`}
        ref={ref}
        onMouseMove={onMouseMove}
        className="group relative block h-full border border-white/5 hover:border-[#D4AF37]/40 transition-colors duration-500 bg-white/[0.02]"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(480px circle at ${pos.x}px ${pos.y}px, rgba(212,175,55,0.09), transparent 70%)` }}
        />
        <div className="clip-corner aspect-[16/10] overflow-hidden">
          <img
            src={p.cover_image}
            alt={p.title}
            className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
          />
        </div>
        <div className="p-8">
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-4">
            <span className="text-[#D4AF37]">{p.category}</span>
            <span className="w-4 h-px bg-slate-600" />
            <span>{p.read_time}</span>
          </div>
          <h3 className="font-display text-xl text-white font-medium leading-tight group-hover:text-[#D4AF37] transition-colors">
            {p.title}
          </h3>
          <p className="mt-3 text-sm text-slate-400 line-clamp-2">{p.excerpt}</p>
        </div>
      </Link>
    </Reveal>
  );
}

export default function Home() {
  useSEO({
    title: "EnPrex Services — ETRM · CTRM · Openlink Endur Consulting & Implementation",
    description: "EnPrex Services is a global specialist consulting firm delivering ETRM, CTRM and Openlink Endur advisory, implementation, cloud modernization and managed services for energy and commodity trading organizations.",
    path: "/",
  });

  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const heroRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 120, damping: 18 });
  const rY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const onHeroMouse = (e) => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div data-testid="home-page">
      {/* HERO — kinetic masked reveal + 3D tilted visual + parallax ghost type */}
      <section
        ref={heroRef}
        onMouseMove={onHeroMouse}
        className="relative min-h-screen flex items-center overflow-hidden -mt-24 pt-24"
      >
        <div aria-hidden className="absolute inset-0">
          <div className="absolute inset-0 bg-[#050A14]" />
          <div
            className="absolute -top-40 -left-40 w-[42rem] h-[42rem] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(37,99,235,0.10), transparent 65%)" }}
          />
          <div
            className="absolute top-1/3 right-0 w-[36rem] h-[36rem] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(212,175,55,0.07), transparent 65%)" }}
          />
          <div className="absolute inset-0 grid-noise opacity-60" />
          <div className="absolute inset-0 lg:hidden">
            <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050A14]/80 to-[#050A14]" />
          </div>
        </div>

        {/* Parallax ghost type */}
        <motion.div
          style={{ y: ghostY }}
          aria-hidden
          className="pointer-events-none absolute -bottom-8 left-0 right-0 overflow-hidden hidden md:block"
        >
          <div
            className="text-center font-display font-bold text-[19vw] leading-[0.8] tracking-tighter text-transparent select-none whitespace-nowrap"
            style={{ WebkitTextStroke: "1px rgba(212,175,55,0.08)" }}
          >
            ENPREX
          </div>
        </motion.div>

        <div className="relative container-x py-20 w-full grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex items-center gap-3 mb-10"
            >
              <div className="w-12 h-px bg-[#D4AF37]" />
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono">
                Energy · Precision · Excellence
              </span>
            </motion.div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl 2xl:text-[4.5vw] font-light text-white leading-[0.98] tracking-tighter">
              <MaskedLines
                delay={0.3}
                lines={[
                  <>Powering the Future of</>,
                  <>Energy &amp; Commodity</>,
                  <span className="text-[#D4AF37] italic font-extralight">Trading.</span>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
              className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed font-light"
            >
              Specialist consulting for energy and commodity trading technology. Deep expertise in ETRM, Openlink Endur and trading technology — helping organisations solve complex problems and deliver with confidence.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1 }}
              className="mt-12 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors active:scale-95"
              >
                Contact Us
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors active:scale-95"
              >
                Explore Services
                <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </motion.div>
          </div>

          {/* 3D tilted, clipped-frame hero visual */}
          <div className="lg:col-span-5 hidden lg:block">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
            >
              <motion.div
                style={{ rotateX: rX, rotateY: rY, y: imgY, transformPerspective: 1200 }}
                className="relative will-change-transform"
              >
                <div aria-hidden className="absolute -inset-3 border border-[#D4AF37]/20" />
                <div className="clip-corner relative overflow-hidden">
                  <img src={HERO_IMG} alt="Trading floor screens showing live market data" className="w-full h-[62vh] object-cover" />
                  <div aria-hidden className="absolute inset-0 bg-[#050A14]/45" />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#050A14]/80 via-transparent to-transparent" />
                </div>
                <div className="mt-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  <span className="w-6 h-px bg-[#D4AF37]/60" aria-hidden />
                  Fig. 01 — Live markets, trading floor
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          aria-hidden
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">Scroll</span>
          <div className="w-px h-12 bg-white/10 overflow-hidden">
            <motion.div
              className="w-px h-1/2 bg-[#D4AF37]"
              animate={{ y: [-24, 48] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <section
        data-testid="editorial-marquee"
        aria-label="Areas of expertise"
        className="border-y border-white/10 bg-[#0B1F3A]/20 py-5 overflow-hidden"
      >
        <div className="marquee-track marquee-slow flex items-center">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0" aria-hidden={copy === 1}>
              {MARQUEE_ITEMS.map((item) => (
                <span key={item} className="flex items-center">
                  <span className="font-mono text-sm uppercase tracking-[0.3em] text-[#D4AF37]/80 whitespace-nowrap px-6">
                    {item}
                  </span>
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37]/40 shrink-0" aria-hidden />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CHAPTER 01 — CREDIBILITY */}
      <section className="border-b border-white/5">
        <div className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <ChapterTag n="01" label="Experience" />
            </div>
          </div>
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05] max-w-2xl">
                Experience that speaks <span className="italic text-[#D4AF37]">for itself</span>.
              </h2>
            </Reveal>
            <div className="mt-16 grid md:grid-cols-2 gap-10">
              <Reveal delay={0.05} data-testid="credibility-orgs" className="border-l border-[#D4AF37]/30 pl-8">
                <div className="font-display text-7xl md:text-8xl font-light text-white tracking-tighter">
                  <CountUp to={20} suffix="+" />
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.25em] text-slate-400 font-mono">Client Organisations</div>
              </Reveal>
              <Reveal delay={0.12} data-testid="credibility-engagements" className="border-l border-[#D4AF37]/30 pl-8">
                <div className="font-display text-7xl md:text-8xl font-light text-white tracking-tighter">
                  <CountUp to={100} suffix="+" />
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.25em] text-slate-400 font-mono">Engagements Delivered</div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 02 — WHAT WE DELIVER */}
      <section className="border-b border-white/5">
        <div className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <ChapterTag n="02" label="What We Deliver" />
            </div>
          </div>
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05] max-w-2xl">
                Two focused <span className="text-[#D4AF37]">practices</span>.
              </h2>
              <p className="mt-8 text-lg text-slate-400 font-light leading-relaxed max-w-2xl">
                Deep expertise in ETRM, Openlink Endur and trading technology — helping organisations solve complex problems and deliver with confidence.
              </p>
              <Link
                to="/services"
                data-testid="home-all-services"
                className="mt-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-semibold text-[#D4AF37] hover:text-[#FDE047] transition-colors border-b border-[#D4AF37] pb-1"
              >
                See all services <ArrowRight size={14} />
              </Link>
            </Reveal>
            <div className="mt-14 grid md:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => (
                <PillarCard key={p.slug} p={p} i={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES + TECH STRIP */}
      <section className="border-b border-white/5 bg-[#0B1F3A]/30">
        <div className="container-x py-16 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-8 font-mono">
              — Industries We Serve
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-6">
              {INDUSTRIES_STRIP.map(({ name, icon: Icon }) => (
                <div key={name} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                  <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.4} />
                  <span className="text-sm font-medium">{name}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-8 font-mono">
              — Technology Expertise
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-6">
              {TECH_STRIP.map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                  <Icon size={20} className="text-slate-300" strokeWidth={1.4} />
                  <span className="text-sm font-medium">{label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CHAPTER 03 — INSIGHTS */}
      <section className="border-b border-white/5">
        <div className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <ChapterTag n="03" label="Insights" />
            </div>
          </div>
          <div className="lg:col-span-9">
            <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
              <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-[1.05]">
                Field notes from the<br />
                <span className="italic">trading floor.</span>
              </h2>
              <Link
                to="/insights"
                data-testid="all-insights-link"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] font-semibold text-[#D4AF37] hover:text-[#FDE047] transition-colors border-b border-[#D4AF37] pb-1 shrink-0"
              >
                All insights <ArrowUpRight size={16} />
              </Link>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <InsightCard key={p.slug} p={p} i={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pt-24 md:pt-32">
        <Reveal>
          <div className="relative overflow-hidden border border-[#D4AF37]/25 bg-gradient-to-br from-[#0B1F3A] to-[#050A14] p-12 md:p-20">
            <Sparkles className="absolute top-8 right-8 text-[#D4AF37]/30" size={32} aria-hidden />
            <div className="max-w-3xl">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-mono">— Partner With EnPrex</div>
              <h2 className="font-display text-3xl md:text-5xl font-light text-white tracking-tighter leading-tight">
                Ready to modernize your <span className="italic text-[#D4AF37]">trading platform</span>?
              </h2>
              <p className="mt-6 text-lg text-slate-300 font-light">
                Speak directly with the practice about your trading technology roadmap — by email or LinkedIn.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="cta-book-consultation"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors active:scale-95"
                >
                  Book a Consultation <ArrowRight size={16} />
                </Link>
                <Link
                  to="/contact"
                  data-testid="cta-request-proposal"
                  className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors active:scale-95"
                >
                  Request a Proposal <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
